import logger from '../utils/logger';
/**
 * REALTIME MANAGER - Gestionnaire Centralisé
 * 
 * Gère toutes les subscriptions Supabase Realtime
 * - Auto-reconnect
 * - Multiplexing (1 channel = N listeners)
 * - Memory efficient
 * - Type-safe
 * 
 * Inspiré de Firebase Realtime Database & Supabase best practices
 */

import { supabase } from './supabase';
import { DataManager } from './data-manager';
import { RealtimeChannel } from '@supabase/supabase-js';

// ============================================
// TYPES
// ============================================

type ChangeType = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface SubscriptionConfig {
 table: string;
 event: ChangeType;
 filter?: string;
 callback: (payload: any) => void;
}

interface ChannelState {
 channel: RealtimeChannel;
 listeners: Set<string>; // listener IDs
 reconnectAttempts: number;
}

// ============================================
// REALTIME MANAGER
// ============================================

class RealtimeManagerClass {
 private channels: Map<string, ChannelState> = new Map();
 private subscriptions: Map<string, SubscriptionConfig> = new Map();
 private userId: string | null = null;
 private isEnabled: boolean = true;

 constructor() {
 logger.log(' [Realtime] Manager initialized');
 }

 // ============================================
 // INITIALIZATION
 // ============================================

 /**
 * Initialize Realtime for a user
 * Called when user logs in
 */
 init(userId: string): void {
 if (this.userId === userId) {
 logger.log(' [Realtime] Already initialized for user:', userId);
 return;
 }

 logger.log(' [Realtime] Initializing for user:', userId);
 this.userId = userId;

 // Setup all subscriptions
 this.setupProfileSubscription(userId);
 this.setupSessionsSubscription(userId);

 logger.log(' [Realtime] Initialization complete');
 }

 /**
 * Cleanup on logout
 */
 destroy(): void {
 logger.log(' [Realtime] Destroying all subscriptions...');

 // Unsubscribe all channels
 this.channels.forEach((state, channelId) => {
 state.channel.unsubscribe();
 });

 this.channels.clear();
 this.subscriptions.clear();
 this.userId = null;

 logger.log(' [Realtime] Destroyed');
 }

 // ============================================
 // PROFILE SUBSCRIPTION
 // ============================================

 /**
 * Subscribe to profile changes
 * Auto-sync with cache when profile updates
 */
 private setupProfileSubscription(userId: string): void {
 const channelId = `profile:${userId}`;

 logger.log(' [Realtime] Setting up profile subscription');

 const channel = supabase
 .channel(channelId)
 .on('postgres_changes', {
 event: '*', // INSERT, UPDATE, DELETE
 schema: 'public',
 table: 'profiles',
 filter: `id=eq.${userId}`,
 }, (payload) => {
 logger.log(' [Realtime] Profile changed:', payload.eventType);
 this.handleProfileChange(payload);
 })
 .subscribe((status) => {
 if (status === 'SUBSCRIBED') {
 logger.log(' [Realtime] Profile subscription active');
 } else if (status === 'CHANNEL_ERROR') {
 logger.error(' [Realtime] Profile subscription error');
 this.handleReconnect(channelId);
 }
 });

 this.channels.set(channelId, {
 channel,
 listeners: new Set(['profile-sync']),
 reconnectAttempts: 0,
 });
 }

 /**
 * Handle profile change from Realtime
 */
 private async handleProfileChange(payload: any): Promise<void> {
 const { eventType, new: newData, old: oldData } = payload;

 switch (eventType) {
 case 'UPDATE':
 logger.log(' [Realtime] Profile updated, syncing cache...');
 if (newData) {
 // Update memory cache immediately
 await DataManager.updateProfile(newData.id, newData);
 }
 break;

 case 'DELETE':
 logger.warn(' [Realtime] Profile deleted');
 // Should trigger logout
 break;
 }
 }

 // ============================================
 // SESSIONS SUBSCRIPTION
 // ============================================

 /**
 * Subscribe to all user sessions
 */
 private setupSessionsSubscription(userId: string): void {
 const channelId = `sessions:${userId}`;

 logger.log(' [Realtime] Setting up sessions subscription');

 const channel = supabase
 .channel(channelId)
 .on('postgres_changes', {
 event: '*',
 schema: 'public',
 table: 'chat_sessions',
 filter: `user_id=eq.${userId}`,
 }, (payload) => {
 logger.log(' [Realtime] Session changed:', payload.eventType);
 this.handleSessionChange(payload);
 })
 .subscribe((status) => {
 if (status === 'SUBSCRIBED') {
 logger.log(' [Realtime] Sessions subscription active');
 } else if (status === 'CHANNEL_ERROR') {
 logger.error(' [Realtime] Sessions subscription error');
 this.handleReconnect(channelId);
 }
 });

 this.channels.set(channelId, {
 channel,
 listeners: new Set(['sessions-sync']),
 reconnectAttempts: 0,
 });
 }

 /**
 * Handle session change from Realtime
 */
 private async handleSessionChange(payload: any): Promise<void> {
 const { eventType, new: newData, old: oldData } = payload;

 switch (eventType) {
 case 'INSERT':
 logger.log(' [Realtime] New session created');
 // Refresh sessions cache
 if (this.userId) {
 await DataManager.getSessions(this.userId);
 }
 break;

 case 'UPDATE':
 logger.log(' [Realtime] Session updated');
 // Update specific session in cache
 break;

 case 'DELETE':
 logger.log(' [Realtime] Session deleted');
 // Remove from cache
 break;
 }
 }

 // ============================================
 // MESSAGES SUBSCRIPTION (per session)
 // ============================================

 /**
 * Subscribe to messages for a specific session
 * Returns unsubscribe function
 */
 subscribeToMessages(
 sessionId: string,
 callback: (message: any) => void
 ): () => void {
 const channelId = `messages:${sessionId}`;

 logger.log(' [Realtime] Subscribing to messages:', sessionId);

 // Check if channel already exists
 if (this.channels.has(channelId)) {
 const state = this.channels.get(channelId)!;
 const listenerId = `msg-${Date.now()}`;
 state.listeners.add(listenerId);

 // Add callback to subscriptions
 this.subscriptions.set(listenerId, {
 table: 'chat_messages',
 event: 'INSERT',
 filter: `session_id=eq.${sessionId}`,
 callback,
 });

 logger.log(' [Realtime] Reusing existing messages channel');

 return () => {
 state.listeners.delete(listenerId);
 this.subscriptions.delete(listenerId);

 // If no more listeners, cleanup channel
 if (state.listeners.size === 0) {
 state.channel.unsubscribe();
 this.channels.delete(channelId);
 logger.log(' [Realtime] Messages channel cleaned up');
 }
 };
 }

 // Create new channel
 const channel = supabase
 .channel(channelId)
 .on('postgres_changes', {
 event: 'INSERT',
 schema: 'public',
 table: 'chat_messages',
 filter: `session_id=eq.${sessionId}`,
 }, (payload) => {
 logger.log(' [Realtime] New message received');
 
 // Notify all listeners
 this.subscriptions.forEach((sub, id) => {
 if (sub.table === 'chat_messages' && id.startsWith('msg-')) {
 sub.callback(payload.new);
 }
 });
 })
 .subscribe((status) => {
 if (status === 'SUBSCRIBED') {
 logger.log(' [Realtime] Messages subscription active');
 } else if (status === 'CHANNEL_ERROR') {
 logger.error(' [Realtime] Messages subscription error');
 this.handleReconnect(channelId);
 }
 });

 const listenerId = `msg-${Date.now()}`;
 this.channels.set(channelId, {
 channel,
 listeners: new Set([listenerId]),
 reconnectAttempts: 0,
 });

 this.subscriptions.set(listenerId, {
 table: 'chat_messages',
 event: 'INSERT',
 filter: `session_id=eq.${sessionId}`,
 callback,
 });

 return () => {
 const state = this.channels.get(channelId);
 if (state) {
 state.listeners.delete(listenerId);
 this.subscriptions.delete(listenerId);

 if (state.listeners.size === 0) {
 state.channel.unsubscribe();
 this.channels.delete(channelId);
 logger.log(' [Realtime] Messages channel cleaned up');
 }
 }
 };
 }

 // ============================================
 // RECONNECTION LOGIC
 // ============================================

 /**
 * Handle reconnection with exponential backoff
 */
 private handleReconnect(channelId: string): void {
 const state = this.channels.get(channelId);
 if (!state) return;

 state.reconnectAttempts++;
 const delay = Math.min(1000 * Math.pow(2, state.reconnectAttempts), 30000);

 logger.warn(` [Realtime] Reconnecting in ${delay}ms (attempt ${state.reconnectAttempts})`);

 setTimeout(() => {
 if (this.channels.has(channelId)) {
 logger.log(' [Realtime] Attempting reconnect:', channelId);
 
 // Resubscribe
 state.channel.subscribe();
 }
 }, delay);
 }

 // ============================================
 // PRESENCE (BONUS: Qui est en ligne)
 // ============================================

 /**
 * Setup presence tracking
 */
 setupPresence(userId: string, metadata: any = {}): () => void {
 logger.log(' [Realtime] Setting up presence');

 const channel = supabase.channel('online-users', {
 config: {
 presence: {
 key: userId,
 },
 },
 });

 channel
 .on('presence', { event: 'sync' }, () => {
 const state = channel.presenceState();
 logger.log(' [Realtime] Presence synced:', Object.keys(state).length, 'users online');
 })
 .on('presence', { event: 'join' }, ({ key, newPresences }) => {
 logger.log(' [Realtime] User joined:', key);
 })
 .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
 logger.log(' [Realtime] User left:', key);
 })
 .subscribe(async (status) => {
 if (status === 'SUBSCRIBED') {
 await channel.track({
 user_id: userId,
 online_at: new Date().toISOString(),
 ...metadata,
 });
 logger.log(' [Realtime] Presence tracking active');
 }
 });

 this.channels.set('presence', {
 channel,
 listeners: new Set(['presence']),
 reconnectAttempts: 0,
 });

 return () => {
 channel.untrack();
 channel.unsubscribe();
 this.channels.delete('presence');
 logger.log(' [Realtime] Presence cleaned up');
 };
 }

 // ============================================
 // UTILITIES
 // ============================================

 /**
 * Get status of all channels
 */
 getStatus(): Record<string, any> {
 const status: Record<string, any> = {};

 this.channels.forEach((state, channelId) => {
 status[channelId] = {
 state: state.channel.state,
 listeners: state.listeners.size,
 reconnectAttempts: state.reconnectAttempts,
 };
 });

 return status;
 }

 /**
 * Enable/disable Realtime globally
 */
 setEnabled(enabled: boolean): void {
 this.isEnabled = enabled;
 logger.log(` [Realtime] ${enabled ? 'Enabled' : 'Disabled'}`);

 if (!enabled) {
 this.destroy();
 } else if (this.userId) {
 this.init(this.userId);
 }
 }

 // ============================================
 // PUBLIC SUBSCRIPTION METHODS (for components)
 // ============================================

 /**
 * Subscribe to session changes with custom callback
 * Compatible API for Studio.tsx
 */
 subscribeSessions(
 userId: string,
 callback: (payload: { eventType: string; new?: any; old?: any }) => void
 ): () => void {
 const channelId = `sessions:${userId}-custom`;

 logger.log(' [Realtime] Custom session subscription');

 // Create custom subscription
 const channel = supabase
 .channel(channelId)
 .on('postgres_changes', {
 event: '*',
 schema: 'public',
 table: 'chat_sessions',
 filter: `user_id=eq.${userId}`,
 }, (payload: any) => {
 callback({
 eventType: payload.eventType,
 new: payload.new,
 old: payload.old,
 });
 })
 .subscribe((status: string) => {
 if (status === 'SUBSCRIBED') {
 logger.log(' [Realtime] Custom sessions subscription active');
 } else if (status === 'CHANNEL_ERROR') {
 logger.error(' [Realtime] Custom sessions subscription error');
 }
 });

 // Store channel
 const listenerId = `sessions-custom-${Date.now()}`;
 this.channels.set(channelId, {
 channel,
 listeners: new Set([listenerId]),
 reconnectAttempts: 0,
 });

 // Return unsubscribe function
 return () => {
 channel.unsubscribe();
 this.channels.delete(channelId);
 logger.log(' [Realtime] Custom sessions subscription cleaned up');
 };
 }

 /**
 * Subscribe to messages with custom callback
 * Compatible API for Studio.tsx
 */
 subscribeMessages(
 sessionId: string,
 callback: (payload: { eventType: string; new?: any; old?: any }) => void
 ): () => void {
 const channelId = `messages:${sessionId}-custom`;

 logger.log(' [Realtime] Custom messages subscription');

 // Create custom subscription
 const channel = supabase
 .channel(channelId)
 .on('postgres_changes', {
 event: '*',
 schema: 'public',
 table: 'chat_messages',
 filter: `session_id=eq.${sessionId}`,
 }, (payload: any) => {
 callback({
 eventType: payload.eventType,
 new: payload.new,
 old: payload.old,
 });
 })
 .subscribe((status: string) => {
 if (status === 'SUBSCRIBED') {
 logger.log(' [Realtime] Custom messages subscription active');
 } else if (status === 'CHANNEL_ERROR') {
 logger.error(' [Realtime] Custom messages subscription error');
 }
 });

 // Store channel
 const listenerId = `messages-custom-${Date.now()}`;
 this.channels.set(channelId, {
 channel,
 listeners: new Set([listenerId]),
 reconnectAttempts: 0,
 });

    // Return unsubscribe function
    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
      logger.log(' [Realtime] Custom messages subscription cleaned up');
    };
  }

  /**
   * Subscribe to insights changes
   */
  subscribeInsights(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `insights:${userId}`;
    logger.log(' [Realtime] Insights subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'insights',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          DataManager.getInsights(userId); // Refresh cache
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Insights subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`insights-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to deep reports changes
   */
  subscribeDeepReports(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `deep_reports:${userId}`;
    logger.log(' [Realtime] Deep reports subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'deep_reports',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          DataManager.getDeepReports(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Deep reports subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`deep_reports-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to business patterns changes
   */
  subscribeBusinessPatterns(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `business_patterns:${userId}`;
    logger.log(' [Realtime] Business patterns subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'business_patterns',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          DataManager.getBusinessPatterns(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Business patterns subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`business_patterns-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to pattern collisions changes
   */
  subscribePatternCollisions(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `pattern_collisions:${userId}`;
    logger.log(' [Realtime] Pattern collisions subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'pattern_collisions',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          DataManager.getPatternCollisions(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Pattern collisions subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`pattern_collisions-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to business maturity scores changes
   */
  subscribeBusinessMaturityScores(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `business_maturity_scores:${userId}`;
    logger.log(' [Realtime] Business maturity scores subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'business_maturity_scores',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          DataManager.getBusinessMaturityScores(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Business maturity scores subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`business_maturity_scores-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to token transactions changes
   */
  subscribeTokenTransactions(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `token_transactions:${userId}`;
    logger.log(' [Realtime] Token transactions subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'token_transactions',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT') {
          DataManager.getTokenTransactions(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Token transactions subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`token_transactions-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to API keys changes
   */
  subscribeApiKeys(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `api_keys:${userId}`;
    logger.log(' [Realtime] API keys subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'api_keys',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
          DataManager.getApiKeys(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] API keys subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`api_keys-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to subscriptions changes
   */
  subscribeSubscriptions(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `subscriptions:${userId}`;
    logger.log(' [Realtime] Subscriptions subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'subscriptions',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          DataManager.getSubscriptions(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Subscriptions subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`subscriptions-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to referrals changes
   */
  subscribeReferrals(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `referrals:${userId}`;
    logger.log(' [Realtime] Referrals subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'referrals',
        filter: `referrer_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT') {
          DataManager.getReferrals(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Referrals subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`referrals-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to dashboards changes
   */
  subscribeDashboards(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `dashboards:${userId}`;
    logger.log(' [Realtime] Dashboards subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'dashboards',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
          DataManager.getDashboards(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] Dashboards subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`dashboards-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }

  /**
   * Subscribe to user files changes
   */
  subscribeUserFiles(
    userId: string,
    callback: (payload: { eventType: string; new?: any; old?: any }) => void
  ): () => void {
    const channelId = `user_files:${userId}`;
    logger.log(' [Realtime] User files subscription');

    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_files',
        filter: `user_id=eq.${userId}`,
      }, (payload: any) => {
        callback({ eventType: payload.eventType, new: payload.new, old: payload.old });
        if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
          DataManager.getUserFiles(userId);
        }
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') logger.log(' [Realtime] User files subscription active');
      });

    this.channels.set(channelId, {
      channel,
      listeners: new Set([`user_files-${Date.now()}`]),
      reconnectAttempts: 0,
    });

    return () => {
      channel.unsubscribe();
      this.channels.delete(channelId);
    };
  }
}

// Singleton instance
export const RealtimeManager = new RealtimeManagerClass();

